/**
 * 신규 평가 오더 등록 폼
 */
import React, { useState, useEffect, useRef } from 'react';
import { API, authFetch } from '../utils/api';
import CustomModal from './CustomModal';

function useIsMobile(bp) { var b = bp || 768; var [m, setM] = useState(window.innerWidth <= b); useEffect(function() { var h = function() { setM(window.innerWidth <= b); }; window.addEventListener('resize', h); return function() { window.removeEventListener('resize', h); }; }, [b]); return m; }

// 기본 검차비 로컬 폴백 (2026-09-02 75,000). 마운트 시 GET /eval/orders/fee-default 로 서버 값을
// 받아 덮어쓴다 — 서버 상수만 바꾸면 프리필이 따라오도록(앱과 동일 규약).
var DEFAULT_FEE = 75000;

var INITIAL_FORM = {
  car_model: '', car_detail: '', car_number: '',
  car_year: new Date().getFullYear() - 5, car_mileage: 0,
  car_color: '', car_fuel: '가솔린',
  seller_name: '', seller_phone: '', seller_type: '개인',
  location: '', location_detail: '',
  inspect_date: '', inspect_time: '오전',
  sale_date: '', requester: '', fee: DEFAULT_FEE, notes: '',
  // 매물 링크 (2026-09-15) — 세 입력 방식 모두의 선택 필드. 링크가 있으면 차량 모델·위치는
  // 평가사가 나중에 채울 수 있어 필수 검사에서 빠진다 (discovery #22).
  listing_url: '',
};

export default function OrderForm({ onSubmit, onCancel, initialData, submitLabel }) {
  var isMobile = useIsMobile();
  var [form, setForm] = useState(initialData ? Object.assign({}, INITIAL_FORM, initialData) : INITIAL_FORM);
  var [loading, setLoading] = useState(false);
  var [ocrLoading, setOcrLoading] = useState(false);
  var [modal, setModal] = useState(null);
  var closeModal = function() { setModal(null); };
  var [addrEmbedOpen, setAddrEmbedOpen] = useState(false);
  var addrEmbedRef = useRef(null);

  // 임베드 모달이 열리면 daum.Postcode를 컨테이너에 embed
  // setTimeout으로 다음 tick에 실행 → ref DOM 마운트 보장
  useEffect(function() {
    if (!addrEmbedOpen) return;
    var t = setTimeout(function() {
      if (!window.daum || !window.daum.Postcode) {
        console.error('[AddrSearch] window.daum.Postcode 미정의 — 스크립트 로드 실패');
        setAddrEmbedOpen(false);
        setModal({ type: 'danger', title: '주소 검색 사용 불가', message: '주소 검색 스크립트를 불러올 수 없습니다. 페이지를 새로고침(Ctrl+Shift+R)해주세요.', hideCancel: true, onConfirm: closeModal });
        return;
      }
      if (!addrEmbedRef.current) {
        console.error('[AddrSearch] embed container ref 미정의');
        return;
      }
      addrEmbedRef.current.innerHTML = '';
      try {
        new window.daum.Postcode({
          oncomplete: function(data) {
            var addr = data.roadAddress || data.jibunAddress || data.address;
            setForm(function(prev) { return Object.assign({}, prev, { location: addr }); });
            setAddrEmbedOpen(false);
          }
        }).embed(addrEmbedRef.current, { autoClose: false });
        console.log('[AddrSearch] embed 호출 완료');
      } catch (err) {
        console.error('[AddrSearch] embed 실패:', err);
        setAddrEmbedOpen(false);
        setModal({ type: 'danger', title: '주소 검색 오류', message: 'embed 실패: ' + err.message, hideCancel: true, onConfirm: closeModal });
      }
    }, 50);
    return function() { clearTimeout(t); };
  }, [addrEmbedOpen]);

  var set = function(key, value) { setForm(function(prev) { return Object.assign({}, prev, { [key]: value }); }); };

  // 신규 등록일 때만 서버 기본 검차비로 프리필 갱신 (수정 모드는 기존 오더 금액 보존).
  // 사용자가 이미 손댄 값(≠ 로컬 폴백)은 덮어쓰지 않는다. 실패 시 로컬 폴백 유지.
  useEffect(function() {
    if (initialData) return;
    var cancelled = false;
    authFetch(API + '/eval/orders/fee-default')
      .then(function(r) { return r.json(); })
      .then(function(j) {
        var n = j && j.data && parseInt(j.data.baseFee, 10);
        if (cancelled || !n || n <= 0) return;
        setForm(function(prev) { return prev.fee === DEFAULT_FEE ? Object.assign({}, prev, { fee: n }) : prev; });
      })
      .catch(function() {});
    return function() { cancelled = true; };
  }, [initialData]);

  // 클립보드 붙여넣기 (Ctrl+V)
  useEffect(function() {
    var handler = function(e) {
      var items = (e.clipboardData || {}).items;
      if (!items) return;
      for (var i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          var file = items[i].getAsFile();
          if (file) { e.preventDefault(); handleHeydealerOCR(file); }
          return;
        }
      }
    };
    document.addEventListener('paste', handler);
    return function() { document.removeEventListener('paste', handler); };
  });

  // Daum Postcode 스크립트는 public/index.html에서 정적 로드 (window.__daumLoaded / window.__daumLoadFailed)
  // popup `.open()` 모드는 일부 환경에서 about:blank로 멈추는 이슈가 있어 임베드 모드 사용
  var openAddressSearch = function() {
    if (window.__daumLoadFailed) {
      setModal({ type: 'danger', title: '주소 검색 사용 불가', message: '주소 검색 스크립트(t1.daumcdn.net)가 차단되었습니다. 네트워크 또는 서버 CSP 설정을 확인해주세요.', hideCancel: true, onConfirm: closeModal });
      return;
    }
    if (!window.daum || !window.daum.Postcode) {
      setModal({ type: 'danger', title: '잠시만요', message: '주소 검색 스크립트를 불러오는 중입니다. 1~2초 후 다시 시도해주세요.', hideCancel: true, onConfirm: closeModal });
      return;
    }
    setAddrEmbedOpen(true);
  };

  var handleHeydealerOCR = function(file) {
    if (!file) return;
    setOcrLoading(true);
    var fd = new FormData(); fd.append('file', file);
    authFetch(API + '/eval/orders/ocr-heydealer', { method: 'POST', body: fd })
      .then(function(r) { return r.json(); })
      .then(function(d) {
        setOcrLoading(false);
        if (d.success && d.data) {
          var r = d.data;
          setForm(function(prev) { return Object.assign({}, prev, {
            car_model: r.carModel || prev.car_model,
            car_detail: r.carDetail || prev.car_detail,
            car_number: r.carNumber || prev.car_number,
            car_year: r.carYear || prev.car_year,
            car_mileage: r.carMileage || prev.car_mileage,
            car_color: r.carColor || prev.car_color,
            car_fuel: r.carFuel || prev.car_fuel,
            seller_phone: r.sellerPhone || prev.seller_phone,
            fee: prev.fee,
            location: r.location || prev.location,
            notes: (function() {
              var lines = [];
              if (r.purchasePrice) lines.push('낙찰가: ' + Number(r.purchasePrice).toLocaleString() + '만원' + (r.newCarPrice ? ' (신차 ' + Number(r.newCarPrice).toLocaleString() + '만원)' : '') + (r.auctionParticipants ? ' / 참여 ' + r.auctionParticipants + '명' : ''));
              if (r.accidentHistory || r.exteriorPanel) lines.push('[사고] ' + (r.accidentHistory || '') + (r.exteriorPanel ? ', 외판 ' + r.exteriorPanel : '') + (r.ownerChange ? ', 소유자 ' + r.ownerChange : ''));
              if (r.repairNeeded) lines.push('[정비] ' + r.repairNeeded);
              if (r.carPros) lines.push('[장점] ' + r.carPros);
              if (r.wheelScratch || r.tireCondition || r.spareKey) lines.push('[상태] ' + (r.wheelScratch ? '휠기스:' + r.wheelScratch : '') + (r.tireCondition ? ' 타이어:' + r.tireCondition : '') + (r.spareKey ? ' 보조키:' + r.spareKey : ''));
              if (r.myCarDamage && r.myCarDamage.length > 0) r.myCarDamage.forEach(function(d) { lines.push('[내차피해] ' + (d.date || '') + ' 수리비 ' + (d.repairCost || 0) + '만원'); });
              if (r.otherCarDamage && r.otherCarDamage.length > 0) r.otherCarDamage.forEach(function(d) { lines.push('[타차가해] ' + (d.date || '') + ' 수리비 ' + (d.repairCost || 0) + '만원'); });
              return lines.join('\n');
            })()
          }); });
        } else { setModal({ type: 'danger', title: '실패', message: 'OCR 실패: ' + (d.error || ''), hideCancel: true, onConfirm: closeModal }); }
      })
      .catch(function(e) { setOcrLoading(false); setModal({ type: 'danger', title: '오류', message: 'OCR 오류: ' + e.message, hideCancel: true, onConfirm: closeModal }); });
  };

  const handleSubmit = async () => {
    // 링크 신청이면 차량 모델·위치는 선택 — 평가사가 링크를 보고/통화해서 채운다 (discovery #22).
    var hasListing = !!(form.listing_url || '').trim();
    if (!form.car_model && !hasListing) { setModal({ type: 'danger', title: '실패', message: '차량 모델 또는 매물 링크를 입력해주세요', hideCancel: true, onConfirm: closeModal }); return; }
    if (!form.location && !hasListing) { setModal({ type: 'danger', title: '실패', message: '검차 위치를 입력해주세요', hideCancel: true, onConfirm: closeModal }); return; }
    setLoading(true);
    try {
      await onSubmit(form);
      setForm(INITIAL_FORM);
    } catch (err) {
      setModal({ type: 'danger', title: '실패', message: '등록 실패: ' + err.message, hideCancel: true, onConfirm: closeModal });
    } finally {
      setLoading(false);
    }
  };

  const fieldStyle = {
    width: '100%', boxSizing: 'border-box', padding: '8px 10px',
    border: '1px solid #e0dfda', borderRadius: 6, fontSize: 13,
    outline: 'none', background: '#fff',
  };

  const labelStyle = {
    fontSize: 11, color: '#888', fontWeight: 500,
    marginBottom: 3, display: 'block',
  };

  return (
    <div style={{
      background: '#fff', border: '2px solid #3B8BD4', borderRadius: 14,
      padding: 20, marginBottom: 16,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#222' }}>
          새 평가 콜 등록
        </h3>
        <button onClick={onCancel} style={{
          background: 'none', border: 'none', fontSize: 20,
          cursor: 'pointer', color: '#888', lineHeight: 1,
        }}>×</button>
      </div>

      {/* 헤이딜러 스크린샷 OCR */}
      <div style={{ background: '#f0f9ff', border: '1px dashed #3B8BD4', borderRadius: 10, padding: 14, marginBottom: 16, textAlign: 'center', cursor: ocrLoading ? 'default' : 'pointer', transition: 'all .15s' }}
        onClick={function() { if (ocrLoading) return; document.getElementById('heydealerOcrInput').click(); }}
        onDragOver={function(e) { e.preventDefault(); e.currentTarget.style.background = '#dbeafe'; }}
        onDragLeave={function(e) { e.currentTarget.style.background = '#f0f9ff'; }}
        onDrop={function(e) { e.preventDefault(); e.currentTarget.style.background = '#f0f9ff'; if (e.dataTransfer.files[0]) handleHeydealerOCR(e.dataTransfer.files[0]); }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#3B8BD4' }}>{ocrLoading ? '🔄 AI 분석 중...' : '📸 헤이딜러 스크린샷으로 자동입력'}</div>
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>스크린샷을 드래그, 클릭, 또는 Ctrl+V로 붙여넣으세요</div>
        <input id="heydealerOcrInput" type="file" accept="image/*" style={{ display: 'none' }} onChange={function(e) { if (e.target.files[0]) handleHeydealerOCR(e.target.files[0]); e.target.value = ''; }} />
      </div>

      {/* 매물 링크 (링크 신청, 2026-09-15) — 스크린샷/직접 입력과 병행 가능한 선택 입력.
          링크만 넣어도 등록되고, 차량 정보는 배정된 평가사가 채운다. */}
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>매물 링크</label>
        <input type="url" value={form.listing_url} onChange={e => set('listing_url', e.target.value)}
          placeholder="https://www.heydealer.com/... (선택)" style={fieldStyle} />
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
          링크만 넣어도 신청됩니다. 차종·차량번호는 배정된 평가사가 입력합니다.
        </div>
      </div>

      {/* 차량 정보 */}
      <div style={{ fontSize: 12, fontWeight: 600, color: '#3B8BD4', marginBottom: 8 }}>차량 정보</div>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10, marginBottom: 16 }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={labelStyle}>차량 모델 *</label>
          <input value={form.car_model} onChange={e => set('car_model', e.target.value)}
            placeholder="예: 현대 소나타" style={fieldStyle} />
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={labelStyle}>세부사양</label>
          <input value={form.car_detail} onChange={e => set('car_detail', e.target.value)}
            placeholder="예: 2.0 GDi 프리미엄" style={fieldStyle} />
        </div>
        <div>
          <label style={labelStyle}>차량번호</label>
          <input value={form.car_number} onChange={e => set('car_number', e.target.value)}
            placeholder="00가0000" style={fieldStyle} />
        </div>
        <div>
          <label style={labelStyle}>연식</label>
          <input type="number" value={form.car_year} onChange={e => set('car_year', +e.target.value)}
            style={fieldStyle} />
        </div>
        <div>
          <label style={labelStyle}>주행거리 (km)</label>
          <input type="number" value={form.car_mileage} onChange={e => set('car_mileage', +e.target.value)}
            style={fieldStyle} />
        </div>
        <div>
          <label style={labelStyle}>색상</label>
          <input value={form.car_color} onChange={e => set('car_color', e.target.value)} style={fieldStyle} />
        </div>
        <div>
          <label style={labelStyle}>연료</label>
          <select value={form.car_fuel} onChange={e => set('car_fuel', e.target.value)} style={fieldStyle}>
            <option>가솔린</option><option>디젤</option><option>LPG</option>
            <option>하이브리드</option><option>전기</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>판매자 연락처</label>
          <input value={form.seller_phone} onChange={e => set('seller_phone', e.target.value)}
            placeholder="010-0000-0000" style={fieldStyle} />
        </div>
      </div>

      {/* 검차 일정 */}
      <div style={{ fontSize: 12, fontWeight: 600, color: '#3B8BD4', marginBottom: 8 }}>검차 일정</div>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10, marginBottom: 16 }}>
        <div>
          <label style={labelStyle}>검차 위치 *</label>
          <div style={{ display: 'flex', gap: 6 }}>
            <input value={form.location} onChange={function(e) { set('location', e.target.value); }}
              placeholder="주소 검색 버튼을 눌러주세요" style={Object.assign({}, fieldStyle, { flex: 1 })} />
            <button type="button" onClick={openAddressSearch} style={{
              padding: '0 12px', borderRadius: 6, border: '1px solid #3B8BD4',
              background: '#E6F1FB', color: '#3B8BD4', fontSize: 12, fontWeight: 600,
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}>검색</button>
          </div>
        </div>
        <div>
          <label style={labelStyle}>상세 위치</label>
          <input value={form.location_detail} onChange={e => set('location_detail', e.target.value)}
            placeholder="아파트 후문 주차장" style={fieldStyle} />
        </div>
        {/* grid 자식 min-width:auto — date input 기본 최소폭이 셀을 벌려 옆 칸을 민다 (bcf46038) */}
        <div style={{ minWidth: 0 }}>
          <label style={labelStyle}>검차일</label>
          <input type="date" value={form.inspect_date} onChange={e => set('inspect_date', e.target.value)}
            style={{ ...fieldStyle, minWidth: 0, WebkitAppearance: 'none' }} />
        </div>
        <div>
          <label style={labelStyle}>시간대</label>
          <select value={form.inspect_time} onChange={e => set('inspect_time', e.target.value)} style={fieldStyle}>
            <option>오전</option><option>오후</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>요청자</label>
          <input value={form.requester} onChange={e => set('requester', e.target.value)}
            placeholder="소미, 수출 등" style={fieldStyle} />
        </div>
        <div>
          <label style={labelStyle}>검차비 (원)</label>
          <input type="number" value={form.fee} onChange={e => set('fee', +e.target.value)} style={fieldStyle} />
        </div>
        <div style={{ minWidth: 0 }}>
          <label style={labelStyle}>판매예정일</label>
          <input type="date" value={form.sale_date} onChange={e => set('sale_date', e.target.value)}
            style={{ ...fieldStyle, minWidth: 0, WebkitAppearance: 'none' }} />
        </div>
      </div>

      {/* 메모 */}
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>메모</label>
        <textarea value={form.notes} onChange={e => set('notes', e.target.value)}
          rows={2} placeholder="특이사항, 차주 연락 가능 시간 등"
          style={{ ...fieldStyle, resize: 'vertical' }} />
      </div>

      {/* 버튼 */}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={onCancel} style={{
          padding: '9px 18px', borderRadius: 8, border: '1px solid #d0d0c8',
          background: '#fff', cursor: 'pointer', fontSize: 13, color: '#555',
        }}>취소</button>
        <button onClick={handleSubmit} disabled={loading} style={{
          padding: '9px 24px', borderRadius: 8, border: 'none',
          background: loading ? '#aaa' : '#3B8BD4', color: '#fff',
          cursor: loading ? 'default' : 'pointer', fontSize: 13, fontWeight: 600,
        }}>{loading ? '처리중...' : (submitLabel || '콜 등록')}</button>
      </div>
      {modal && <CustomModal isOpen={true} {...modal} onCancel={closeModal} />}
      {addrEmbedOpen && (
        <div onClick={function(e) { if (e.target === e.currentTarget) setAddrEmbedOpen(false); }}
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: '#fff', borderRadius: 12, width: 480, maxWidth: '92vw', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#1B3A5C' }}>주소 검색</span>
              <button type="button" onClick={function() { setAddrEmbedOpen(false); }}
                style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#64748b', lineHeight: 1, padding: 0 }}>×</button>
            </div>
            <div ref={addrEmbedRef} style={{ width: '100%', height: 460, background: '#fff' }} />
          </div>
        </div>
      )}
    </div>
  );
}
